import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { createLowlight } from 'lowlight';
import TurndownService from 'turndown';
import { marked } from 'marked';
import api from '../api';

// Create lowlight instance with common languages
const lowlight = createLowlight();

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ value, onChange, placeholder }) => {
  // Initialize Turndown service for HTML to Markdown conversion
  const turndownService = useMemo(() => {
    return new TurndownService({
      codeBlockStyle: 'fenced',
      fence: '```',
    });
  }, []);

  // Configure marked for markdown to HTML conversion
  useEffect(() => {
    marked.setOptions({
      breaks: true,
      gfm: true,
    });
  }, []);

  // Track if update is coming from editor (to prevent circular updates)
  const isInternalUpdate = useRef(false);
  
  // Toggle between WYSIWYG and markdown views
  const [showMarkdown, setShowMarkdown] = useState(false);

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const formData = new FormData();
    let fileName = file.name;
    if (!fileName) {
      const extension = file.type.split('/')[1] || 'png';
      fileName = `pasted-image-${Date.now()}.${extension}`;
    }
    formData.append('image', file, fileName);

    try {
      const response = await api.post('/uploads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.url;
    } catch (error: any) {
      console.error('Image upload error:', error);
      throw new Error(error.response?.data?.error || error.message || 'Failed to upload image');
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // We'll use CodeBlockLowlight instead
      }),
      CodeBlockLowlight.configure({
        lowlight: lowlight,
        defaultLanguage: 'plaintext',
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-brand hover:underline',
        },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse border border-gray-300 dark:border-gray-600 my-4',
        },
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 dark:border-gray-600',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 font-semibold p-2',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 dark:border-gray-600 p-2',
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      // Convert HTML to Markdown for storage
      isInternalUpdate.current = true;
      const html = editor.getHTML();
      const markdown = turndownService.turndown(html);
      onChange(markdown);
      // Reset flag after a short delay to allow state update to complete
      setTimeout(() => {
        isInternalUpdate.current = false;
      }, 0);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[150px] p-4 text-sm',
        placeholder: placeholder || 'Type here...',
      },
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            handleImageUpload(file).then((url) => {
              const { schema } = view.state;
              const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
              if (coordinates) {
                const node = schema.nodes.image.create({ src: url });
                const transaction = view.state.tr.insert(coordinates.pos, node);
                view.dispatch(transaction);
              }
            }).catch((error) => {
              console.error('Failed to upload image:', error);
              alert('Failed to upload image: ' + error.message);
            });
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event, slice) => {
        const items = Array.from(event.clipboardData?.items || []);
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) {
              handleImageUpload(file).then((url) => {
                const { schema } = view.state;
                const { selection } = view.state;
                const node = schema.nodes.image.create({ src: url });
                const transaction = view.state.tr.replaceSelectionWith(node);
                view.dispatch(transaction);
              }).catch((error) => {
                console.error('Failed to upload image:', error);
                alert('Failed to upload image: ' + error.message);
              });
            }
            return true;
          }
        }
        return false;
      },
    },
  });

  // Update editor content when value prop changes (but not from internal updates)
  useEffect(() => {
    if (editor && !isInternalUpdate.current && !showMarkdown) {
      const currentHtml = editor.getHTML();
      const currentMarkdown = turndownService.turndown(currentHtml);
      // Only update if the value actually changed and it's not from our own update
      if (value !== currentMarkdown && value.trim() !== currentMarkdown.trim()) {
        // Convert markdown to HTML for Tiptap
        try {
          const html = marked.parse(value) as string;
          editor.commands.setContent(html, false);
        } catch (error) {
          console.error('Error parsing markdown:', error);
          // Fallback: try to set as plain text
          editor.commands.setContent(value, false);
        }
      }
    }
  }, [value, editor, turndownService, showMarkdown]);

  const addImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          const url = await handleImageUpload(file);
          editor?.chain().focus().setImage({ src: url }).run();
        } catch (error: any) {
          alert('Failed to upload image: ' + error.message);
        }
      }
    };
    input.click();
  }, [editor, handleImageUpload]);

  if (!editor) {
    return null;
  }

  return (
    <div className="w-full min-w-0 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
        {/* Toggle between WYSIWYG and Markdown */}
        <div className="flex items-center gap-1 mr-2 border-r border-gray-300 dark:border-gray-600 pr-2">
          <button
            type="button"
            tabIndex={-1}
            onClick={() => {
              if (showMarkdown) {
                // Switching from markdown to WYSIWYG - convert markdown to HTML
                try {
                  isInternalUpdate.current = true;
                  const html = marked.parse(value) as string;
                  if (editor) {
                    editor.commands.setContent(html, false);
                  }
                  setTimeout(() => {
                    isInternalUpdate.current = false;
                  }, 0);
                } catch (error) {
                  console.error('Error parsing markdown:', error);
                }
              } else {
                // Switching from WYSIWYG to markdown - convert HTML to markdown
                if (editor) {
                  const html = editor.getHTML();
                  const markdown = turndownService.turndown(html);
                  isInternalUpdate.current = true;
                  onChange(markdown);
                  setTimeout(() => {
                    isInternalUpdate.current = false;
                  }, 0);
                }
              }
              setShowMarkdown(!showMarkdown);
            }}
            className={`px-3 py-1 text-xs font-medium rounded ${
              showMarkdown
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                : 'bg-brand-muted text-brand-muted-text'
            } hover:bg-gray-200 dark:hover:bg-gray-700`}
            title={showMarkdown ? 'Switch to WYSIWYG' : 'Switch to Markdown'}
          >
            {showMarkdown ? 'WYSIWYG' : 'Markdown'}
          </button>
        </div>
        {!showMarkdown && (
          <>
        {/* Text Formatting */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={showMarkdown}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('bold') ? 'bg-gray-200 dark:bg-gray-700' : ''
          } ${showMarkdown ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Bold"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 4a1 1 0 011-1h4.5a3.5 3.5 0 014 3.5 3.5 3.5 0 01-2.5 3.5H6v2h4a1 1 0 110 2H6v2h4a1 1 0 110 2H6a1 1 0 01-1-1V4z" />
          </svg>
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={showMarkdown}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('italic') ? 'bg-gray-200 dark:bg-gray-700' : ''
          } ${showMarkdown ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Italic"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8 2a1 1 0 000 2h2l-2 12H6a1 1 0 100 2h8a1 1 0 100-2h-2l2-12h2a1 1 0 100-2H8z" />
          </svg>
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('strike') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Strikethrough"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        {/* Headings */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('heading', { level: 1 }) ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Heading 1"
        >
          H1
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('heading', { level: 2 }) ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('heading', { level: 3 }) ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Heading 3"
        >
          H3
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        {/* Lists */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Bullet List"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Numbered List"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('blockquote') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Quote"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V7a1 1 0 112 0v3.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        {/* Code */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('code') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Inline Code"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('codeBlock') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Code Block"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd" />
            <path fillRule="evenodd" d="M8 6a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm0 4a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        {/* Links and Images */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => {
            const url = window.prompt('Enter URL:');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('link') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Add Link"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
          </svg>
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={addImage}
          className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
          title="Insert Image"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-700 mx-1" />

        {/* Table */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('table') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Insert Table"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2v2h2V6H5zm4 0v2h2V6H9zm4 0v2h2V6h-2zM5 10v2h2v-2H5zm4 0v2h2v-2H9zm4 0v2h2v-2h-2zM5 14v2h2v-2H5zm4 0v2h2v-2H9zm4 0v2h2v-2h-2z" clipRule="evenodd" />
          </svg>
        </button>
        {editor.isActive('table') && (
          <>
            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => editor.chain().focus().addColumnBefore().run()}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Add Column Before"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Add Column After"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => editor.chain().focus().deleteColumn().run()}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Delete Column"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => editor.chain().focus().addRowBefore().run()}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Add Row Before"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ transform: 'rotate(90deg)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => editor.chain().focus().addRowAfter().run()}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Add Row After"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ transform: 'rotate(-90deg)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => editor.chain().focus().deleteRow().run()}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Delete Row"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ transform: 'rotate(90deg)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => editor.chain().focus().deleteTable().run()}
              className="p-2 rounded hover:bg-red-200 dark:hover:bg-red-700"
              title="Delete Table"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
              className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
                editor.isActive('tableHeader') ? 'bg-gray-200 dark:bg-gray-700' : ''
              }`}
              title="Toggle Header Column"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => editor.chain().focus().toggleHeaderRow().run()}
              className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
                editor.isActive('tableHeader') ? 'bg-gray-200 dark:bg-gray-700' : ''
              }`}
              title="Toggle Header Row"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ transform: 'rotate(90deg)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
          </>
        )}

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-700 mx-1" />

        {/* Horizontal Rule */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
          title="Horizontal Rule"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>
          </>
        )}
      </div>

      {/* Editor Content */}
      <div className="min-h-[300px] max-h-[600px] overflow-y-auto text-sm">
        {showMarkdown ? (
          <textarea
            value={value}
            onChange={(e) => {
              isInternalUpdate.current = true;
              onChange(e.target.value);
              setTimeout(() => {
                isInternalUpdate.current = false;
              }, 0);
            }}
            placeholder={placeholder || 'Type markdown here...'}
            className="w-full h-full min-h-[300px] p-4 font-mono text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-0 focus:outline-none resize-none"
            style={{ fontFamily: 'monospace' }}
          />
        ) : (
          <EditorContent editor={editor} />
        )}
        <style>{`
          /* Code block styling - Light mode */
          .ProseMirror pre {
            background: #1e293b !important;
            color: #f8fafc !important;
            padding: 1rem;
            border-radius: 0.375rem;
            overflow-x: auto;
            margin: 1rem 0;
            border: 1px solid #cbd5e1;
          }
          /* Code block styling - Dark mode */
          .dark .ProseMirror pre {
            background: #0f172a !important;
            color: #f8fafc !important;
            border-color: #475569;
          }
          /* Code inside pre blocks */
          .ProseMirror pre code {
            background: transparent !important;
            padding: 0;
            font-size: 0.875rem;
            line-height: 1.5;
            color: #f8fafc !important;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
          }
          .dark .ProseMirror pre code {
            color: #f8fafc !important;
          }
          /* Inline code styling */
          .ProseMirror code:not(pre code) {
            background: #f1f5f9;
            color: #0f172a;
            padding: 0.125rem 0.375rem;
            border-radius: 0.25rem;
            font-size: 0.875em;
            font-weight: 500;
          }
          .dark .ProseMirror code:not(pre code) {
            background: #334155;
            color: #e2e8f0;
          }
          /* Override any conflicting styles */
          .ProseMirror pre * {
            color: #f8fafc !important;
          }
          .dark .ProseMirror pre * {
            color: #f8fafc !important;
          }
          /* Image styling */
          .ProseMirror img {
            max-width: 100%;
            height: auto;
            border-radius: 0.5rem;
            margin: 1rem 0;
          }
          /* Link styling */
          .ProseMirror a {
            color: #4f46e5;
            text-decoration: underline;
          }
          .dark .ProseMirror a {
            color: #818cf8;
          }
          /* Table styling */
          .ProseMirror table {
            border-collapse: collapse;
            margin: 1rem 0;
            table-layout: fixed;
            width: 100%;
          }
          .ProseMirror table td,
          .ProseMirror table th {
            min-width: 1em;
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            vertical-align: top;
            box-sizing: border-box;
            position: relative;
          }
          .dark .ProseMirror table td,
          .dark .ProseMirror table th {
            border-color: #4b5563;
          }
          .ProseMirror table th {
            font-weight: bold;
            text-align: left;
            background-color: #f3f4f6;
          }
          .dark .ProseMirror table th {
            background-color: #374151;
          }
          .ProseMirror table .selectedCell:after {
            z-index: 2;
            position: absolute;
            content: "";
            left: 0; right: 0; top: 0; bottom: 0;
            background: rgba(200, 200, 255, 0.4);
            pointer-events: none;
          }
          .dark .ProseMirror table .selectedCell:after {
            background: rgba(100, 100, 200, 0.4);
          }
          .ProseMirror table .column-resize-handle {
            position: absolute;
            right: -2px;
            top: 0;
            bottom: -2px;
            width: 4px;
            background-color: #adf;
            pointer-events: none;
          }
        `}</style>
      </div>
    </div>
  );
};

export default MarkdownEditor;
